import { useEffect, useRef, useState } from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { pexelsSrc, pexelsSrcSet } from "@/lib/pexelsImage";

interface PexelsImage {
  src: string;
  alt: string;
  photographer: string;
  photographerUrl: string;
}

interface Listing {
  title: string;
  link: string;
  type: string;
  typeLabel: string;
  bullets: string[];
  image: PexelsImage | null;
}

interface Props {
  platform: string;
  url: string;
  /** Restrict listings to this city (e.g. "Stuttgart") */
  city?: string;
}

export const PortalListingsCarousel = ({ platform, url, city }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [listings, setListings] = useState<Listing[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    let active = true;
    const cacheKey = `portal-offers:${url}:${city ?? ""}`;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) { setListings(JSON.parse(cached)); setIsLoading(false); }
      else setIsLoading(true);
    } catch { setIsLoading(true); }

    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("portal-carousel", {
          body: { platform, url, city },
        });
        if (error) throw error;
        if (!active) return;
        const fresh = Array.isArray(data?.listings) ? data.listings : [];
        setListings(fresh);
        try { if (fresh.length) localStorage.setItem(cacheKey, JSON.stringify(fresh)); } catch { /* storage full */ }
        setNote(data?.note ?? null);
      } catch (error) {
        console.error("Portal-Carousel Fehler:", error);
        if (active) setNote("Angebote konnten nicht geladen werden.");
      } finally {
        if (active) setIsLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [inView, platform, url, city]);

  return (
    <div ref={containerRef} className="mb-4 border-t border-border pt-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
        </span>
        <h4 className="text-sm font-semibold text-card-foreground">Aktuelle Angebote bei {platform}</h4>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-28 w-full rounded-md" />
          <Skeleton className="h-3 w-2/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      ) : listings.length > 0 ? (
        <Carousel opts={{ align: "start" }} className="w-full">
          <CarouselContent>
            {listings.map((listing, index) => (
              <CarouselItem key={`${listing.link}-${index}`}>
                <article className="rounded-md border border-border bg-muted/30 overflow-hidden">
                  {listing.image && (
                    <figure className="m-0">
                      <img
                        src={pexelsSrc(listing.image.src, 480)}
                        srcSet={pexelsSrcSet(listing.image.src)}
                        sizes="(min-width: 768px) 33vw, 100vw"
                        alt={listing.image.alt}
                        loading={index === 0 ? "eager" : "lazy"}
                        decoding="async"
                        width={480}
                        height={320}
                        className="w-full aspect-video object-cover bg-muted"
                      />
                      <figcaption className="px-3 pt-1 text-[10px] leading-tight text-muted-foreground">
                        Stockfoto – klicken Sie auf „Angebot ansehen“ für echte Bilder. Foto:{" "}
                        <a
                          href={listing.image.photographerUrl}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="underline underline-offset-2"
                        >
                          {listing.image.photographer}
                        </a>{" "}
                        / Pexels
                      </figcaption>
                    </figure>
                  )}
                  <div className="p-3">
                    <Badge variant="secondary" className="mb-2 text-[10px]">
                      {listing.typeLabel}
                    </Badge>
                    <h5 className="text-sm font-semibold leading-snug line-clamp-2 mb-2">
                      {listing.title}
                    </h5>
                    <ul className="space-y-1 mb-3">
                      {listing.bullets.slice(0, 3).map((bullet, i) => (
                        <li key={i} className="flex gap-2 text-xs text-muted-foreground">
                          <span className="text-primary">•</span>
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                    <a
                      href={listing.link}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-medium text-primary underline underline-offset-2 hover:no-underline"
                    >
                      Angebot ansehen
                      <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                    </a>
                  </div>
                </article>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="-left-3 h-11 w-11 [&_svg]:h-5 [&_svg]:w-5" />
          <CarouselNext className="-right-3 h-11 w-11 [&_svg]:h-5 [&_svg]:w-5" />
        </Carousel>
      ) : (
        <p className="text-xs text-muted-foreground">
          {note ?? "Derzeit keine Live-Angebote abrufbar."}{" "}
          <a
            href={url}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="text-primary underline underline-offset-2 inline-flex items-center gap-1"
          >
            Zur Portalsuche
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        </p>
      )}
    </div>
  );
};
